import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

import {Typography} from '@nur-portal/ui-kit';

import {FEATURE_NAMES} from '../../../growthbook/constants';
import {handleLoginEvent} from '../../../modules/auth-buttons';
import {getCookie, setCookie} from '../../../services/cookie';
import {getHoursToNextDay} from '../../../services/dates';
import {dispatchArticleAuthModal} from '../../components/article-auth-modal/constants';
import ArticleReactionsList from '../../components/article-reactions-list';
import {ArticleReactionOnboarding} from '../../components/article-reactions-onboarding';
import {
  ARTICLE_REACTION_ONBOARDING_COOKIES,
  ARTICLE_REACTION_ONBOARDING_LONG_TTL_HOURS,
  GROWTHBOOK_ACTIVE_VALUES
} from '../../components/article-reactions-onboarding/constants';
import {useGrowthbook} from '../../hooks/use-growthbook';
import {useUser} from '../../hooks/use-user';
import {useArticleReactions} from '../../hooks/useArticleReactions';
import {articleBottomNavMemoryRepository} from '../../repositories/article-bottom-nav-memory-repository';
import '../../i18n/config';

import styles from './article-reactions.module.css';

const multiplierForViewPort = 2;

const ArticleReactions = ({articleId, withOnBoarding = false}) => {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const containerRef = useRef(null);
  const observableRef = useRef(null);
  const growthBook = useGrowthbook();

  const growthBookValue = growthBook?.getFeatureValue(FEATURE_NAMES.REACTIONS_ONBOARDING);
  const growthbookEnabled = GROWTHBOOK_ACTIVE_VALUES.includes(growthBookValue);
  const isReactionsOnBoardingExistsInCookie = getCookie(
    ARTICLE_REACTION_ONBOARDING_COOKIES[window.Site.targeting.section]
  );
  const observerRef = useRef(new IntersectionObserver(entires => {
    if (window.Site.targeting.platform === 'smartphone') {
      const viewPortHeight = window.innerHeight;
      const isVisibleBottomNav = articleBottomNavMemoryRepository.get()?.[articleId]?.isVisible;

      entires.forEach(entry => {
        if (entry.isIntersecting && isVisibleBottomNav && entry.boundingClientRect.top <= viewPortHeight) {
          articleBottomNavMemoryRepository.set(articleId, false);
          return;
        }

        if (!entry.isIntersecting
          && !isVisibleBottomNav
          && multiplierForViewPort * viewPortHeight > entry.boundingClientRect.top
          && entry.boundingClientRect.top > 0) {
          articleBottomNavMemoryRepository.set(articleId, true);
          return;
        }
      });
    }
  }, {
    rootMargin: '0px 0px 0px 0px'
  }));
  const {t} = useTranslation();
  const {isAuthenticated} = useUser();

  const {allReactions, userReaction, onSetUserReaction} = useArticleReactions(articleId);

  const onReactionClick = reactionType => {
    if (!isAuthenticated) {
      handleLoginEvent(() => onSetUserReaction(reactionType));
      dispatchArticleAuthModal();
      return;
    }

    onSetUserReaction(reactionType);
  };

  useEffect(() => {
    if (observableRef.current) {
      observerRef.current.observe(observableRef.current);
    }
  }, [observableRef]);

  const onboardingObserverRef = useRef(new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting
        && !isReactionsOnBoardingExistsInCookie
        && withOnBoarding
        && growthbookEnabled) {
        setCookie(ARTICLE_REACTION_ONBOARDING_COOKIES[window.Site?.targeting.section], true, getHoursToNextDay());
        setShowOnboarding(true);
      }
    });
  }, {
    rootMargin: '-50% 0% -50% 0%'
  }));

  useEffect(() => {
    if (containerRef) {
      onboardingObserverRef.current.observe(containerRef.current);
    }

    return () => {
      onboardingObserverRef.current.disconnect();
    };
  }, [containerRef]);

  const onCloseOnboarding = () => {
    setCookie(
      ARTICLE_REACTION_ONBOARDING_COOKIES[window.Site?.targeting.section],
      true,
      ARTICLE_REACTION_ONBOARDING_LONG_TTL_HOURS
    );
  };

  const onRemoveOnboardingHandler = () => {
    setShowOnboarding(false);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Typography variant="title-21" className={styles.title}>
        {t('articleReactions.title')}
      </Typography>
      <div ref={observableRef} className={styles.listContainer}>
        <ArticleReactionsList
          onReactionClick={reactionType => onReactionClick(reactionType)}
          userReaction={userReaction || ''}
          currentReactions={allReactions || {}}
        />
        {showOnboarding && (
          <ArticleReactionOnboarding
            textVariant={growthBookValue}
            onClose={onCloseOnboarding}
            isOpen={showOnboarding}
            onRemoveOnboarding={onRemoveOnboardingHandler}
          />
        )}
      </div>
    </div>
  );
};

ArticleReactions.propTypes = {
  articleId: PropTypes.string.isRequired
};

export default ArticleReactions;
